import React from 'react';
import Icon from '../../../components/AppIcon';

const StepIndicator = ({ steps, currentStep, onStepClick }) => {
  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'active';
    return 'upcoming';
  };

  const getStepIcon = (step, status) => {
    if (status === 'completed') return 'CheckCircle';
    return step?.icon;
  };

  return (
    <div className="w-full bg-card border-b border-border">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-6">
        <div className="hidden lg:flex items-center justify-between">
          {steps?.map((step, index) => {
            const status = getStepStatus(index);
            const isClickable = index < currentStep;
            
            return (
              <React.Fragment key={step?.id}>
                <button
                  onClick={() => isClickable && onStepClick(index)}
                  disabled={!isClickable}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-md
                    transition-athletic
                    ${status === 'active' ? 'bg-primary text-primary-foreground' : ''}
                    ${status === 'completed' ? 'bg-success/10 text-success hover:bg-success/20' : ''}
                    ${status === 'upcoming' ? 'bg-muted text-muted-foreground' : ''}
                    ${isClickable ? 'cursor-pointer hover-lift' : 'cursor-not-allowed'}
                  `}
                >
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center
                    ${status === 'active' ? 'bg-primary-foreground/20' : ''}
                    ${status === 'completed' ? 'bg-success/20' : ''}
                    ${status === 'upcoming' ? 'bg-muted-foreground/10' : ''}
                  `}>
                    <Icon 
                      name={getStepIcon(step, status)} 
                      size={20}
                      color={status === 'active' ? 'var(--color-primary-foreground)' : status === 'completed' ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                    />
                  </div>
                  <div className="text-left">
                    <div className="text-sm caption opacity-80">Step {index + 1}</div>
                    <div className="font-heading font-medium">{step?.title}</div>
                  </div>
                </button>
                {index < steps?.length - 1 && (
                  <div className={`
                    flex-1 h-0.5 mx-2
                    ${index < currentStep ? 'bg-success' : 'bg-border'}
                  `} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="lg:hidden">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm caption text-muted-foreground">
              Step {currentStep + 1} of {steps?.length}
            </div>
            <div className="text-sm font-medium data-text text-foreground">
              {Math.round(((currentStep + 1) / steps?.length) * 100)}%
            </div>
          </div>
          
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden mb-4">
            <div 
              className="h-full bg-primary transition-athletic"
              style={{ width: `${((currentStep + 1) / steps?.length) * 100}%` }}
            />
          </div>
          
          <div className="flex items-center gap-3 p-4 bg-primary/10 rounded-md border border-primary/20">
            <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <Icon 
                name={steps?.[currentStep]?.icon} 
                size={24}
                color="var(--color-primary-foreground)"
              />
            </div>
            <div className="flex-1">
              <div className="text-sm caption text-muted-foreground mb-1">
                Current Step
              </div>
              <div className="font-heading font-semibold text-foreground">
                {steps?.[currentStep]?.title}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepIndicator;